import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  Button,
} from '@mui/material';
import {
  Cancel,
  Receipt,
  Info,
  HelpOutline,
} from '@mui/icons-material';
import paymentService from '../services/paymentService';
import { PaymentStatusResponse } from '../types';

const PublicPaymentCancelPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const factureId = searchParams.get('factureId');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusResponse | null>(null);

  // Log de debug
  useEffect(() => {
    console.log('📍 PublicPaymentCancelPage - URL:', window.location.href);
    console.log('📍 Paramètres URL:', Object.fromEntries(searchParams.entries()));
    console.log('📍 factureId extrait:', factureId);
  }, [searchParams, factureId]);

  useEffect(() => {
    const fetchPaymentStatus = async () => {
      if (!factureId) {
        setError('Identifiant de facture manquant dans l\'URL');
        setLoading(false);
        return;
      }

      // Vérifier que factureId est un nombre valide
      const factureIdNumber = Number(factureId);
      if (isNaN(factureIdNumber) || factureIdNumber <= 0) {
        setError(`Identifiant de facture invalide: ${factureId}`);
        setLoading(false);
        return;
      }

      try {
        const status = await paymentService.getPaymentStatus(factureIdNumber);
        setPaymentStatus(status);
      } catch (err: any) {
        console.error('Erreur lors de la récupération du statut:', err);
        setError(
          err.response?.data?.message ||
          'Impossible de vérifier le statut du paiement'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentStatus();
  }, [factureId]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Vérification du statut...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Typography variant="body1" color="text.secondary">
              Si vous avez des questions, veuillez contacter votre prestataire.
            </Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        py: 6,
        px: 3,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={3}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Header avec annulation */}
          <Box
            sx={{
              bgcolor: 'warning.main',
              color: 'white',
              py: 4,
              px: 3,
              textAlign: 'center',
            }}
          >
            <Cancel sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Paiement annulé
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Votre paiement n'a pas été effectué
            </Typography>
          </Box>

          {/* Corps du message */}
          <Box sx={{ p: 4 }}>
            <Stack spacing={3}>
              {/* Informations de la facture */}
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Receipt color="primary" />
                  Informations
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">
                      Numéro de facture
                    </Typography>
                    <Typography fontWeight={600}>
                      {paymentStatus?.factureNumero || 'N/A'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography color="text.secondary">
                      Statut
                    </Typography>
                    <Typography fontWeight={600} color="warning.main">
                      En attente de paiement
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Message de confirmation */}
              <Alert severity="info" icon={<Info />}>
                <Typography variant="body2" fontWeight={500}>
                  Aucun montant n'a été débité de votre compte
                </Typography>
              </Alert>

              {/* Que faire maintenant */}
              <Box>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HelpOutline color="primary" />
                  Que faire maintenant ?
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <Alert severity="warning">
                    <Stack spacing={1}>
                      <Typography variant="body2" fontWeight={600}>
                        Vous avez plusieurs options :
                      </Typography>
                      <Typography variant="body2">
                        • Utilisez à nouveau le lien de paiement pour réessayer
                      </Typography>
                      <Typography variant="body2">
                        • Contactez votre prestataire pour obtenir un nouveau lien
                      </Typography>
                      <Typography variant="body2">
                        • Réglez votre facture par un autre moyen de paiement
                      </Typography>
                    </Stack>
                  </Alert>

                  <Box
                    sx={{
                      bgcolor: 'info.50',
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'info.200',
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Besoin d'aide ?
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Si vous rencontrez des difficultés pour effectuer le paiement,
                      n'hésitez pas à contacter votre prestataire qui pourra vous assister.
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              {/* Note de sécurité */}
              <Box
                sx={{
                  bgcolor: 'grey.50',
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200',
                }}
              >
                <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                  Cette transaction a été traitée de manière sécurisée par Stripe.
                  Aucune information bancaire n'a été enregistrée.
                </Typography>
              </Box>

              {/* Bouton de fermeture */}
              <Box sx={{ textAlign: 'center', pt: 2 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => window.close()}
                  sx={{ minWidth: 200 }}
                >
                  Fermer cette page
                </Button>
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PublicPaymentCancelPage;
